export type HereStyle =
  | "explore.day"
  | "satellite.day"
  | "explore.satellite.day";

export type HereFormat = "png8" | "png" | "jpg" | "jpeg";
export type PPI = 72 | 92 | 120 | 160 | 320;
export type TileSize = 256; // 256 only
export type Catalog = "base" | "aerial";

export type TileUrlOpts = {
  apiKey?: string;
  baseUrl?: string;
  catalog?: Catalog;
  style: HereStyle;
  format?: HereFormat;
  size?: TileSize;
  ppi?: PPI;
  lang?: string;
  lg?: string;
  lg2?: string;
  pview?: string;
};

// ---------- env & normalization ----------
const RAW_BASE = (process.env.REACT_APP_HERE_MAPS_URL ?? "").trim();
const ENV_API_KEY = (process.env.REACT_APP_HERE_MAPS_API_KEY ?? "").trim();
const ENV_IMAGERY_CATALOG = (
  process.env.REACT_APP_HERE_MAPS_IMAGERY_CATALOG ?? ""
)
  .trim()
  .toLowerCase();

// Safe default if env not provided
const DEFAULT_BASE = (process.env.REACT_APP_HERE_MAPS_URL || "https://maps.hereapi.com/v3").trim();

const warnOnce = (() => {
  const fired = new Set<string>();
  return (key: string, msg: string) => {
    if (fired.has(key)) return;
    // eslint-disable-next-line no-console
    console.warn(msg);
    fired.add(key);
  };
})();

const stripTrailingSlash = (u: string) => u.replace(/\/+$/, "");
const ensureV3 = (u: string) => {
  const clean = stripTrailingSlash(u || DEFAULT_BASE);
  return /\/v3$/.test(clean) ? clean : `${clean}/v3`;
};

function normalizeBase(u?: string) {
  const src = (u && u.trim()) || RAW_BASE || DEFAULT_BASE;
  const norm = ensureV3(src);
  if (!u && (!RAW_BASE || ensureV3(RAW_BASE) !== norm)) {
    warnOnce(
      "baseUrl",
      "[HERE] Using default/normalized REACT_APP_HERE_MAPS_URL. Check your .env (expected .../v3)."
    );
  }
  return norm;
}

function requireApiKey(k?: string) {
  const key = (k ?? ENV_API_KEY).trim();
  if (!key)
    warnOnce(
      "apikey",
      "[HERE] Missing REACT_APP_HERE_MAPS_API_KEY. Requests will 401."
    );
  return key;
}

// ---------- logic helpers ----------
const isSatelliteStyle = (s: HereStyle) =>
  s === "satellite.day" || s === "explore.satellite.day";

function pickCatalog(style: HereStyle, explicit?: Catalog): Catalog {
  if (explicit) return explicit;
  if (
    isSatelliteStyle(style) &&
    (ENV_IMAGERY_CATALOG === "aerial" || ENV_IMAGERY_CATALOG === "base")
  ) {
    return ENV_IMAGERY_CATALOG as Catalog;
  }
  return "base";
}

/** Coerce format per catalog (default base→png8, aerial→jpg) but allow png8|png|jpg either way. */
function coerceFormat(
  fmt: HereFormat | undefined,
  catalog: Catalog
): "png8" | "png" | "jpg" {
  if (fmt === "jpeg") fmt = "jpg";
  if (!fmt) return catalog === "aerial" ? "jpg" : "png8";
  return fmt === "png8" || fmt === "png" || fmt === "jpg"
    ? fmt
    : catalog === "aerial"
    ? "jpg"
    : "png8";
}

function buildQuery(opts: TileUrlOpts): string {
  const qs = new URLSearchParams();
  qs.set("style", opts.style);
  qs.set("apiKey", requireApiKey(opts.apiKey));
  qs.set("size", "256"); // 256 only
  if (!isSatelliteStyle(opts.style) && opts.ppi) qs.set("ppi", String(opts.ppi));
  qs.set("lang", opts.lang ?? "en");
  if (opts.lg) qs.set("lg", opts.lg);
  if (opts.lg2) qs.set("lg2", opts.lg2);
  if (opts.pview) qs.set("pview", opts.pview);
  return qs.toString();
}

/** Overloads: legacy (apiKey, style, format?) OR modern (options object) */
export function getHereTileUrl(
  apiKey: string,
  style: HereStyle,
  format?: Exclude<HereFormat, "jpeg">
): string;
export function getHereTileUrl(opts: TileUrlOpts): string;

export function getHereTileUrl(
  arg1: string | TileUrlOpts,
  style?: HereStyle,
  format?: Exclude<HereFormat, "jpeg">
): string {
  const opts: TileUrlOpts =
    typeof arg1 === "string"
      ? (() => {
          if (!style)
            throw new Error(
              "getHereTileUrl(apiKey, style, format?) requires a style."
            );
          return { apiKey: arg1, style, format };
        })()
      : arg1;

  const base = normalizeBase(opts.baseUrl);
  const catalog = pickCatalog(opts.style, opts.catalog);
  const fmt = coerceFormat(opts.format, catalog);
  const qs = buildQuery(opts);
  return `${base}/${catalog}/mc/{z}/{x}/{y}/${fmt}?${qs}`;
}

export function getHereTileUrlAt(
  z: number,
  x: number,
  y: number,
  opts: TileUrlOpts
): string {
  const base = normalizeBase(opts.baseUrl);
  const catalog = pickCatalog(opts.style, opts.catalog);
  const fmt = coerceFormat(opts.format, catalog);
  const qs = buildQuery(opts);
  return `${base}/${catalog}/mc/${z}/${x}/${y}/${fmt}?${qs}`;
}

// ---------- convenience: bundle common URLs for Leaflet layers ----------
export function buildHereUrls(params?: {
  apiKey?: string;
  baseUrl?: string;
  lang?: string;
  ppi?: PPI;
  imageryCatalog?: Catalog;
}) {
  const common: Pick<TileUrlOpts, "apiKey" | "baseUrl" | "lang" | "ppi"> = {
    apiKey: params?.apiKey,
    baseUrl: params?.baseUrl,
    lang: params?.lang ?? "en",
    ppi: params?.ppi,
  };

  const imageryCatalog: Catalog =
    params?.imageryCatalog ||
    (ENV_IMAGERY_CATALOG === "aerial" || ENV_IMAGERY_CATALOG === "base"
      ? (ENV_IMAGERY_CATALOG as Catalog)
      : "base");

  return {
    day: getHereTileUrl({ ...common, style: "explore.day", catalog: "base" }),
    sat: getHereTileUrl({
      ...common,
      style: "satellite.day",
      catalog: imageryCatalog,
    }),
    hybrid: getHereTileUrl({
      ...common,
      style: "explore.satellite.day",
      catalog: imageryCatalog,
    }),
  };
}
